import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { 
  FaCheckCircle, 
  FaCreditCard, 
  FaShieldAlt, 
  FaInfoCircle,
  FaUser,
  FaPhone,
  FaBirthdayCake,
  FaWallet,
} from 'react-icons/fa';
import { useCurrency } from '../../context/CurrencyContext';
import { useAuth } from '../../context/AuthContext';
import { formatPriceSync } from '../../utils/currency';

const API_BASE = (() => {
  const base = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || 'http://localhost:3000';
  return typeof base === 'string' ? base.replace(/\/api\/activity\/?$/, '') : 'http://localhost:3000';
})();

const PriceSummary = ({
  selectedCountry,
  selectedCity,
  selectedActivity,
  selectedOption,
  selectedDate,
  selectedTime,
  selectedSlot,
  priceBreakdown,
  visitors,
  visitorDetails,
  handleVisitorDetailChange,
  onBookingComplete,
}) => {
  const { currency } = useCurrency();
  const { user } = useAuth();
  const authdata = useSelector((state) => state?.auth?.authdata);
  const isLoggedIn = Boolean(user || authdata?.token);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [walletBalance, setWalletBalance] = useState(0);
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [showPayConfirm, setShowPayConfirm] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) return;
    setLoadingWallet(true);
    axios
      .get(`${API_BASE}/api/wallet/my-balance`, { withCredentials: true })
      .then((res) => {
        if (res.data?.success && res.data?.data?.[0]) {
          setWalletBalance(Number(res.data.data[0].total) || 0);
        } else {
          setWalletBalance(0);
        }
      })
      .catch(() => setWalletBalance(0))
      .finally(() => setLoadingWallet(false));
  }, [isLoggedIn]);

  const totalAmount = priceBreakdown?.total || 0;
  const canPayWithWallet = isLoggedIn && walletBalance >= totalAmount && !loadingWallet;
  const adultMinAge = visitors.length ? Math.max(...visitors.map((v) => Number(v.minAge) || 0)) : 18;
  const allDetailsFilled = priceBreakdown?.breakdown?.every((item) => {
    const visitorType = visitors.find(v => v.type === item.type);
    if (!visitorType || item.count === 0) return true;
    const typeDetails = visitorDetails[visitorType.typeId] || [];
    const isChild = (Number(visitorType.maxAge) || 0) < adultMinAge;
    return typeDetails.length === item.count && typeDetails.every((detail) => {
      if (!detail.name || detail.age === undefined || detail.age === '') return false;
      if (!isChild && !detail.phone) return false;
      return true;
    });
  });

  if (!priceBreakdown) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <FaInfoCircle className="w-16 h-16 mx-auto opacity-50" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No price breakdown available
        </h3>
        <p className="text-gray-600">
          Please go back and calculate the price first
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Summary */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quick Summary Card */}
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
              <div>
                <h3 className="text-xl font-bold mb-0.5">Booking summary</h3>
                <p className="text-blue-100 text-sm">Review details before payment</p>
              </div>
              <div className="text-right">
                <div className="text-2xl sm:text-3xl font-bold">
                  {formatPriceSync(priceBreakdown?.total || 0, currency)}
                </div>
                <div className="text-blue-200 text-xs sm:text-sm">Total amount</div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-white/20">
              <div>
                <div className="text-xs text-blue-200 uppercase tracking-wide">Activity</div>
                <div className="font-semibold truncate">{selectedActivity?.name || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-blue-200 uppercase tracking-wide">Date</div>
                <div className="font-semibold">{selectedDate || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-blue-200 uppercase tracking-wide">Time</div>
                <div className="font-semibold">{selectedSlot ? `${selectedSlot.startTime} – ${selectedSlot.endTime}` : (selectedTime || "—")}</div>
              </div>
              <div>
                <div className="text-xs text-blue-200 uppercase tracking-wide">Guests</div>
                <div className="font-semibold">{visitors.reduce((sum, v) => sum + v.count, 0) || 0}</div>
              </div>
            </div>
          </div>

          {/* Visitor Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">Visitor details</h3>
              <span className="text-sm text-slate-500">Name, phone & age for each guest</span>
            </div>

            <div className="space-y-6">
              {priceBreakdown?.breakdown && priceBreakdown.breakdown.length > 0 ? (
                priceBreakdown.breakdown.map((item, breakdownIndex) => {
                  const visitorType = visitors.find(v => v.type === item.type);
                  if (!visitorType || item.count === 0) return null;
                
                const typeDetails = visitorDetails[visitorType.typeId] || [];
                const isChildType = (Number(visitorType.maxAge) || 0) < adultMinAge;
                return (
                    <div key={breakdownIndex} className="border border-slate-200 rounded-xl p-5 bg-slate-50/50">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="font-semibold text-gray-900">{item.type} ({visitorType.minAge}-{visitorType.maxAge})</h4>
                        <p className="text-sm text-gray-600">
                          {item.count} {item.count === 1 ? 'person' : 'people'}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900">
                          {formatPriceSync(item.subtotal || 0, currency)}
                        </div>
                        <div className="text-sm text-gray-500">Subtotal</div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Array.from({ length: item.count }).map((_, index) => (
                        <div key={index} className="bg-white rounded-xl p-4 border border-slate-100">
                          <div className="flex items-center mb-3">
                            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-3">
                              {index + 1}
                            </div>
                            <span className="font-medium text-gray-900">
                              {item.type} #{index + 1}
                            </span>
                          </div>
                          
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                                <FaUser className="mr-1" size={10} />
                                Full Name
                              </label>
                              <input
                                type="text"
                                value={typeDetails[index]?.name || ''}
                                onChange={(e) => handleVisitorDetailChange(visitorType.typeId, index, 'name', e.target.value)}
                                className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-400 text-sm transition-colors"
                                placeholder="Enter full name"
                              />
                            </div>
                            
                            {!isChildType && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                                <FaPhone className="mr-1" size={10} />
                                Phone Number
                              </label>
                              <input
                                type="tel"
                                value={typeDetails[index]?.phone || ''}
                                onChange={(e) => handleVisitorDetailChange(visitorType.typeId, index, 'phone', e.target.value)}
                                className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-400 text-sm transition-colors"
                                placeholder="Enter phone number"
                              />
                            </div>
                            )}
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                                <FaBirthdayCake className="mr-1" size={10} />
                                Age
                              </label>
                              <input
                                type="number"
                                min={visitorType.minAge}
                                max={visitorType.maxAge}
                                value={typeDetails[index]?.age || ''}
                                onChange={(e) => handleVisitorDetailChange(visitorType.typeId, index, 'age', e.target.value)}
                                className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-400 text-sm transition-colors"
                                placeholder={`${visitorType.minAge}-${visitorType.maxAge}`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
                })
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                  <FaInfoCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No visitor breakdown available. Please go back and recalculate.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-6">
            {/* Payment Card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900">Payment Summary</h3>
                <div className="p-2 bg-green-100 rounded-lg">
                  <FaCheckCircle className="text-green-600" />
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Base Price</span>
                  <span className="font-medium">
                    {formatPriceSync(priceBreakdown?.breakdown?.[0]?.basePrice || 0, currency)}
                  </span>
                </div>
                
                {priceBreakdown?.timeMultiplier && priceBreakdown.timeMultiplier !== 1 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Time Multiplier</span>
                    <span className="font-medium">×{priceBreakdown.timeMultiplier}</span>
                  </div>
                )}

                {priceBreakdown?.breakdown?.map((item, index) => (
                  item.visitorMultiplier && item.visitorMultiplier !== 1 && (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-gray-600">{item.type} Multiplier</span>
                      <span className="font-medium">×{item.visitorMultiplier}</span>
                    </div>
                  )
                ))}

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900">Total Amount</span>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">
                        {formatPriceSync(priceBreakdown?.total || 0, currency)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {priceBreakdown?.currency || selectedCountry?.currency || ''} • Including all taxes
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment method: Card or Wallet */}
              <div className="mb-4">
                <div className="text-sm font-semibold text-gray-700 mb-2">Payment method</div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('card')}
                    className={`flex-1 py-2.5 px-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
                      paymentMethod === 'card'
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <FaCreditCard /> Card
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('wallet')}
                    disabled={!isLoggedIn}
                    className={`flex-1 py-2.5 px-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
                      paymentMethod === 'wallet'
                        ? 'bg-emerald-600 text-white shadow-md'
                        : isLoggedIn ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <FaWallet /> Wallet
                  </button>
                </div>
                {paymentMethod === 'wallet' && (
                  <div className="mt-2 text-sm text-gray-600">
                    Balance: {formatPriceSync(walletBalance, currency)}
                    {!isLoggedIn && <span className="text-amber-600 ml-1">(Login to use wallet)</span>}
                    {isLoggedIn && walletBalance < totalAmount && (
                      <span className="text-amber-600 ml-1">Insufficient balance. Add funds or use card.</span>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => setShowPayConfirm(true)}
                  disabled={
                    !allDetailsFilled ||
                    (paymentMethod === 'wallet' && !canPayWithWallet)
                  }
                  className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {paymentMethod === 'wallet' ? (
                    <>
                      <FaWallet className="mr-1" />
                      Pay with Wallet
                    </>
                  ) : (
                    <>
                      <FaCreditCard className="mr-1" />
                      Pay Now
                    </>
                  )}
                </button>

                {showPayConfirm && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Payment</h3>
                      {paymentMethod === 'wallet' ? (
                        <p className="text-gray-600 mb-4">
                          Amount <strong>{formatPriceSync(totalAmount, currency)}</strong> will be deducted from your wallet. Confirm?
                        </p>
                      ) : (
                        <p className="text-gray-600 mb-4">
                          You will be charged <strong>{formatPriceSync(totalAmount, currency)}</strong>. Proceed?
                        </p>
                      )}
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setShowPayConfirm(false)}
                          className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowPayConfirm(false);
                            onBookingComplete(paymentMethod);
                          }}
                          className="flex-1 py-2.5 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700"
                        >
                          Confirm
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <button type="button" className="w-full py-3 border-2 border-blue-500 text-blue-600 rounded-xl font-medium hover:bg-blue-50 transition-colors">
                  Save for Later
                </button>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center text-sm text-gray-600 mb-3">
                  <FaShieldAlt className="mr-2 text-blue-500" />
                  <span className="font-medium">Secure Payment</span>
                </div>
                <p className="text-xs text-gray-500">
                  Your payment is secured with 256-bit SSL encryption. We never store your card details.
                </p>
              </div>
            </div>

            {/* Need Help Card */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-100 p-6">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-white rounded-lg shadow-sm mr-3">
                  <FaInfoCircle className="text-blue-600" />
                </div>
                <h4 className="font-bold text-gray-900">Need Help?</h4>
              </div>
              
              <div className="space-y-3">
                <div className="text-sm text-gray-600">
                  <div className="font-medium mb-1">Customer Support</div>
                  <div>support@adventurebook.com</div>
                  <div>+1 (234) 567-8900</div>
                </div>
                
                <div className="text-sm text-gray-600">
                  <div className="font-medium mb-1">Cancellation Policy</div>
                  <div>Free cancellation up to 24 hours before activity</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceSummary;